import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUserStore } from '@/stores/userStore';
import { useUploadTaskStore } from '../store/uploadTaskStore';
import { useBeforeUnloadGuard } from '../hooks/useBeforeUnloadGuard';
import { reconcileUploadTasks, resetUploadTasks } from '../services/materialUploadTask';
import { registerUploadTaskStorageFlush } from '../core/persistStorage';
import type { UploadTaskDuplicateItem } from '../core/types';

// 页面隐藏 / 卸载时立刻把节流中的任务快照落盘（模块级，只注册一次）
registerUploadTaskStorageFlush();

/**
 * 上传任务全局宿主。
 *
 * 挂载在 `App.tsx` 中 `AppRouter` 之外（与 `<Toaster />` / `<ConfirmDialog />` 同级）
 * → 切换路由不会卸载，上传与守卫都能延续。
 *
 * 职责：
 * 1. 页面刷新 / 关闭拦截（仅存在进行中任务时）。
 * 2. 应用启动时对本地持久化的任务做一次断点对账（幂等）。
 * 3. 用户登出 / 切换账号时清空任务与上传内核，避免用错凭证或数据串号。
 * 4. 全局「保存结果」重复素材 Dialog —— 抽屉可能已关闭，不能留在抽屉里渲染。
 */
export function UploadTaskHost() {
  useBeforeUnloadGuard();
  const userId = useUserStore((state) => state.userInfo?.userId);
  const prevUserIdRef = useRef<string | undefined>(userId);

  useEffect(() => {
    // userId 变化（登出 / 换账号）时清空任务与上传实例
    if (prevUserIdRef.current && prevUserIdRef.current !== userId) {
      resetUploadTasks();
    }
    prevUserIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    // 启动即对账：浏览器被强关 / 关机导致任务没回写服务端时，在这里补上
    void reconcileUploadTasks();
  }, []);

  // 「保存结果」Dialog：任一任务出现 duplicates 即弹出（抽屉可能已关闭）
  const tasks = useUploadTaskStore((state) => state.tasks);
  const [duplicatesResult, setDuplicatesResult] = useState<UploadTaskDuplicateItem[] | null>(null);
  const settledDuplicateTaskIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const target = tasks.find(
      (task) =>
        (task.status === 'success' || task.status === 'failed') &&
        !!task.duplicates?.length &&
        !settledDuplicateTaskIdsRef.current.has(task.taskId)
    );
    if (target?.duplicates?.length) {
      settledDuplicateTaskIdsRef.current.add(target.taskId);
      setDuplicatesResult(target.duplicates);
    }
  }, [tasks]);

  return (
    <Dialog
      open={duplicatesResult !== null}
      onOpenChange={(open) => {
        if (!open) setDuplicatesResult(null);
      }}
    >
      <DialogContent className="w-[40vw] max-w-[40vw]! min-w-120 max-h-[85vh] flex flex-col p-6">
        <DialogHeader>
          <DialogTitle>保存结果</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          <div className="text-sm">
            已过滤{' '}
            <span className="text-orange-600 font-semibold">{duplicatesResult?.length ?? 0}</span>{' '}
            个重复素材
          </div>

          {duplicatesResult && duplicatesResult.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">重复素材明细</div>
              <div className="max-h-[40vh] overflow-y-auto rounded border">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="border-b p-2 text-left font-medium">素材名称</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicatesResult.map((item) => (
                      <tr key={item.materialId} className="align-top">
                        <td className="border-b p-2 break-all">{item.materialName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => setDuplicatesResult(null)}>我知道了</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
